import { normalizeName } from '../shared/naming';
import { materialNaturalKey, type Material } from './material';
import { unitNaturalKey, type UnitOfMeasure } from './unit-of-measure';

/**
 * Importação do catálogo de materiais.
 *
 * Quem já opera uma obra tem o cadastro de itens no sistema de compras, com os códigos
 * que a equipe usa no dia a dia. Redigitar isso aqui seria trabalho perdido e, pior,
 * criaria códigos divergentes entre os dois sistemas — exatamente o que o RN-010 existe
 * para evitar.
 *
 * Tudo aqui é **puro**: recebe as linhas já lidas do arquivo e devolve o que seria feito.
 * Nada toca o banco antes da confirmação (RN-030) — a leitura do arquivo fica atrás de
 * uma porta, e a gravação, no caso de uso.
 */

export type MaterialImportField = 'code' | 'name' | 'unit' | 'category' | 'description';

export interface ImportColumn {
  readonly field: MaterialImportField;
  readonly label: string;
  readonly required: boolean;
  /** Cabeçalhos que costumam nomear esta coluna nos sistemas de compras. */
  readonly synonyms: readonly string[];
}

/**
 * Colunas reconhecidas.
 *
 * "Descrição" mapeia para o **nome**, não para a descrição: nos sistemas de compras é
 * ela que traz "Bloco cerâmico 19x30". O campo de descrição fica com os cabeçalhos de
 * detalhamento, que são o complemento longo.
 */
export const MATERIAL_IMPORT_COLUMNS: readonly ImportColumn[] = [
  {
    field: 'code',
    label: 'Código',
    required: true,
    synonyms: [
      'codigo',
      'cod',
      'código do item',
      'codigo do item',
      'codigo material',
      'sku',
      'referencia',
      'ref',
      'item',
    ],
  },
  {
    field: 'name',
    label: 'Nome',
    required: true,
    synonyms: [
      'descricao',
      'descricao do item',
      'nome',
      'material',
      'produto',
      'insumo',
      'descricao do material',
    ],
  },
  {
    field: 'unit',
    label: 'Unidade',
    required: false,
    synonyms: ['unidade', 'un', 'und', 'unid', 'unidade de medida', 'um', 'medida'],
  },
  {
    field: 'category',
    label: 'Categoria',
    required: false,
    synonyms: ['categoria', 'grupo', 'familia', 'classe', 'segmento', 'linha'],
  },
  {
    field: 'description',
    label: 'Detalhamento',
    required: false,
    synonyms: ['detalhamento', 'especificacao', 'complemento', 'observacao', 'obs', 'detalhe'],
  },
];

/** Compara cabeçalhos ignorando acento, caixa e espaço — "Descrição" casa com "descricao". */
function headerKey(value: string): string {
  return normalizeName(value)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export type ColumnMapping = Partial<Record<MaterialImportField, string>>;

/**
 * Sugere o mapeamento a partir dos cabeçalhos do arquivo.
 *
 * É sugestão, não decisão: a tela mostra o que foi entendido e deixa corrigir antes de
 * qualquer gravação. Uma coluna só é usada por um campo — o primeiro que a reivindicar.
 */
export function suggestColumnMapping(headers: readonly string[]): ColumnMapping {
  const mapping: ColumnMapping = {};
  const taken = new Set<string>();

  for (const column of MATERIAL_IMPORT_COLUMNS) {
    const match = headers.find((header) => {
      if (taken.has(header)) return false;
      const key = headerKey(header);
      return key === headerKey(column.label) || column.synonyms.includes(key);
    });
    if (match !== undefined) {
      mapping[column.field] = match;
      taken.add(match);
    }
  }

  return mapping;
}

/**
 * Apelidos de unidade aceitos na importação.
 *
 * O sistema de compras raramente usa o mesmo símbolo: escreve "PC" onde aqui é "un" e
 * "LT" onde aqui é "L". Traduzir na entrada é melhor do que recusar a linha e obrigar a
 * editar a planilha.
 */
const UNIT_ALIASES: Readonly<Record<string, string>> = {
  pc: 'un',
  pç: 'un',
  peca: 'un',
  peça: 'un',
  pecas: 'un',
  unidade: 'un',
  und: 'un',
  unid: 'un',
  saco: 'sc',
  sacos: 'sc',
  sc: 'sc',
  lt: 'L',
  l: 'L',
  litro: 'L',
  litros: 'L',
  ton: 't',
  tonelada: 't',
  toneladas: 't',
  mt: 'm',
  metro: 'm',
  metros: 'm',
  m2: 'm2',
  'm²': 'm2',
  m3: 'm3',
  'm³': 'm3',
  conjunto: 'cj',
  cjto: 'cj',
  verba: 'vb',
  kilo: 'kg',
  quilo: 'kg',
  // O orçamento do Park Europa (Fase 26). `ml` ali é **metro linear**, e não mililitro: é
  // como a construção escreve o metro de rodapé e de tubo; `unid.` chega com o ponto, que
  // a normalização não tira; `Tb` é o tubo, que ganhou unidade própria na `0052`.
  ml: 'm',
  'm.l.': 'm',
  'unid.': 'un',
  'und.': 'un',
  'pç.': 'un',
  'pc.': 'un',
  tubo: 'tb',
  tubos: 'tb',
  rl: 'rolo',
  rolos: 'rolo',
  // Os terceirizados por tempo e por ponto. `hh` continua sendo o homem-hora: só a hora
  // escrita por extenso vira `h`.
  hora: 'h',
  horas: 'h',
  hr: 'h',
  diária: 'diaria',
  diárias: 'diaria',
  diarias: 'diaria',
  ponto: 'pt',
  pontos: 'pt',
};

/** Resolve a unidade escrita no arquivo contra o catálogo. */
export function resolveUnit(
  raw: string,
  units: readonly UnitOfMeasure[],
): UnitOfMeasure | undefined {
  const normalized = normalizeName(raw);
  if (normalized === '') return undefined;

  const target = UNIT_ALIASES[normalized] ?? normalized;
  const key = unitNaturalKey(target);

  return (
    units.find((unit) => unitNaturalKey(unit.code) === key) ??
    units.find((unit) => normalizeName(unit.name) === key)
  );
}

export type ImportRowStatus = 'CREATE' | 'UPDATE' | 'UNCHANGED' | 'INVALID' | 'DUPLICATE';

export interface ImportIssue {
  readonly field: MaterialImportField | 'row';
  readonly message: string;
}

export interface MaterialImportRow {
  /** Número da linha no arquivo, contando o cabeçalho — é o que o usuário enxerga. */
  readonly rowNumber: number;
  readonly status: ImportRowStatus;
  readonly code: string;
  readonly name: string;
  readonly unitId: string | undefined;
  readonly unitLabel: string;
  readonly category: string | undefined;
  readonly description: string | undefined;
  /** Material existente com o mesmo código, quando houver (RN-011). */
  readonly existingId: string | undefined;
  readonly issues: readonly ImportIssue[];
}

export interface ClassifyInput {
  readonly rows: readonly Record<string, string>[];
  readonly mapping: ColumnMapping;
  readonly units: readonly UnitOfMeasure[];
  readonly existing: readonly Material[];
  /** Unidade usada quando o arquivo não traz coluna de unidade ou não a reconhece. */
  readonly fallbackUnitId?: string | undefined;
}

export interface ImportPlan {
  readonly rows: readonly MaterialImportRow[];
  readonly summary: {
    readonly total: number;
    readonly create: number;
    readonly update: number;
    readonly unchanged: number;
    readonly invalid: number;
    readonly duplicate: number;
  };
}

/**
 * Classifica cada linha do arquivo.
 *
 * O que decide o destino de uma linha:
 *
 * - sem código ou sem nome → `INVALID`; são os dois campos que dão identidade ao item;
 * - código repetido **dentro do arquivo** → `DUPLICATE`, e só a primeira ocorrência vale
 *   — importar as duas criaria uma disputa pelo mesmo código no índice único;
 * - código que já existe no catálogo → `UPDATE`, ou `UNCHANGED` se nada mudaria;
 * - o resto → `CREATE`.
 *
 * Nenhuma linha inválida derruba as demais: o arquivo do mundo real vem com sujeira, e
 * recusar tudo por causa de três linhas seria trocar um problema pequeno por um grande.
 */
export function classifyImport(input: ClassifyInput): ImportPlan {
  const existingByCode = new Map(
    input.existing.map((material) => [materialNaturalKey(material.code), material]),
  );
  const seenInFile = new Set<string>();
  const rows: MaterialImportRow[] = [];

  input.rows.forEach((raw, index) => {
    const rowNumber = index + 2; // +1 pelo cabeçalho, +1 porque humano conta do 1
    const issues: ImportIssue[] = [];

    const pick = (field: MaterialImportField): string => {
      const column = input.mapping[field];
      return column === undefined ? '' : (raw[column] ?? '').trim();
    };

    const code = pick('code');
    const name = pick('name');
    const unitRaw = pick('unit');
    const category = pick('category');
    const description = pick('description');

    if (code === '') issues.push({ field: 'code', message: 'Código vazio.' });
    if (name === '') issues.push({ field: 'name', message: 'Nome vazio.' });

    const resolvedUnit = resolveUnit(unitRaw, input.units);
    const unitId = resolvedUnit?.id ?? input.fallbackUnitId;
    if (resolvedUnit === undefined) {
      if (input.fallbackUnitId === undefined) {
        issues.push({
          field: 'unit',
          message:
            unitRaw === ''
              ? 'Sem unidade. Escolha uma unidade padrão para as linhas sem unidade.'
              : `Unidade "${unitRaw}" não existe no catálogo.`,
        });
      }
    }

    const key = materialNaturalKey(code);
    const isDuplicate = code !== '' && seenInFile.has(key);
    if (code !== '') seenInFile.add(key);

    const existing = code === '' ? undefined : existingByCode.get(key);

    const unitLabel = resolvedUnit?.name ?? (unitRaw === '' ? '—' : unitRaw);

    let status: ImportRowStatus;
    if (issues.length > 0) {
      status = 'INVALID';
    } else if (isDuplicate) {
      status = 'DUPLICATE';
      issues.push({ field: 'code', message: 'Código repetido dentro do arquivo.' });
    } else if (existing !== undefined) {
      const changed =
        existing.name !== name ||
        (unitId !== undefined && existing.unitId !== unitId) ||
        (existing.category ?? '') !== category ||
        (existing.description ?? '') !== description;
      status = changed ? 'UPDATE' : 'UNCHANGED';
    } else {
      status = 'CREATE';
    }

    rows.push({
      rowNumber,
      status,
      code,
      name,
      unitId,
      unitLabel,
      category: category === '' ? undefined : category,
      description: description === '' ? undefined : description,
      existingId: existing?.id,
      issues,
    });
  });

  return {
    rows,
    summary: {
      total: rows.length,
      create: rows.filter((row) => row.status === 'CREATE').length,
      update: rows.filter((row) => row.status === 'UPDATE').length,
      unchanged: rows.filter((row) => row.status === 'UNCHANGED').length,
      invalid: rows.filter((row) => row.status === 'INVALID').length,
      duplicate: rows.filter((row) => row.status === 'DUPLICATE').length,
    },
  };
}

export const IMPORT_STATUS_LABEL: Readonly<Record<ImportRowStatus, string>> = {
  CREATE: 'Novo',
  UPDATE: 'Atualiza',
  UNCHANGED: 'Sem mudança',
  INVALID: 'Com erro',
  DUPLICATE: 'Repetido',
};
